export class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled = true;
  private volume = 0.5;
  private bgmEnabled = true;
  private bgmVolume = 0.25;
  private bgmOscillators: OscillatorNode[] = [];
  private bgmGains: GainNode[] = [];
  private bgmTimeout: number | null = null;
  private isPlayingBgm = false;

  // Cheerful arcade chiptune melody (C major, 8th notes at 140 BPM)
  private readonly bgmMelody = [
    { n: 523.25, d: 0.25 }, { n: 587.33, d: 0.25 }, { n: 659.25, d: 0.25 }, { n: 783.99, d: 0.25 },
    { n: 659.25, d: 0.25 }, { n: 587.33, d: 0.25 }, { n: 523.25, d: 0.25 }, { n: 392.00, d: 0.25 },
    { n: 440.00, d: 0.25 }, { n: 493.88, d: 0.25 }, { n: 523.25, d: 0.25 }, { n: 440.00, d: 0.25 },
    { n: 392.00, d: 0.25 }, { n: 440.00, d: 0.25 }, { n: 523.25, d: 0.5 }, { n: 0, d: 0.25 },
  ];
  private bgmIndex = 0;

  init(): void {
    if (typeof window === 'undefined') return;
    this.ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.stopBgm();
    } else if (this.bgmEnabled && !this.isPlayingBgm) {
      this.startBgm();
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setBgmEnabled(enabled: boolean): void {
    this.bgmEnabled = enabled;
    if (!enabled) {
      this.stopBgm();
    } else if (this.enabled && !this.isPlayingBgm) {
      this.startBgm();
    }
  }

  isBgmEnabled(): boolean {
    return this.bgmEnabled;
  }

  setBgmVolume(volume: number): void {
    this.bgmVolume = Math.max(0, Math.min(1, volume));
    this.bgmGains.forEach((gain) => {
      if (gain?.gain) {
        gain.gain.setValueAtTime(this.bgmVolume * 0.3, this.ctx?.currentTime || 0);
      }
    });
  }

  getBgmVolume(): number {
    return this.bgmVolume;
  }

  setSfxVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  getSfxVolume(): number {
    return this.volume;
  }

  resume(): void {
    if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
    if (this.enabled && this.bgmEnabled && !this.isPlayingBgm) {
      this.startBgm();
    }
  }

  startBgm(): void {
    if (!this.enabled || !this.bgmEnabled || !this.ctx || this.isPlayingBgm) return;
    this.isPlayingBgm = true;
    this.playBgmNote();
  }

  stopBgm(): void {
    this.isPlayingBgm = false;
    if (this.bgmTimeout) {
      window.clearTimeout(this.bgmTimeout);
      this.bgmTimeout = null;
    }
    this.bgmOscillators.forEach((osc) => {
      try { osc.stop(); } catch { /* already stopped */ }
    });
    this.bgmOscillators = [];
    this.bgmGains = [];
  }

  private playBgmNote(): void {
    if (!this.isPlayingBgm || !this.ctx) return;

    const note = this.bgmMelody[this.bgmIndex];
    this.bgmIndex = (this.bgmIndex + 1) % this.bgmMelody.length;

    if (note.n > 0) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'square';
      osc.frequency.setValueAtTime(note.n, this.ctx.currentTime);

      // Soft chiptune tone with quick attack/decay
      gain.gain.setValueAtTime(0, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(this.bgmVolume * 0.15, this.ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + note.d * 0.9);

      // Lowpass filter to soften the square wave
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 1200;

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start();
      osc.stop(this.ctx.currentTime + note.d * 0.95);

      this.bgmOscillators.push(osc);
      this.bgmGains.push(gain);

      // Clean up stopped oscillators
      osc.onended = () => {
        const idx = this.bgmOscillators.indexOf(osc);
        if (idx >= 0) {
          this.bgmOscillators.splice(idx, 1);
          this.bgmGains.splice(idx, 1);
        }
      };
    }

    this.bgmTimeout = window.setTimeout(() => this.playBgmNote(), note.d * 1000);
  }

  play(name: 'drop' | 'grab' | 'success' | 'fail' | 'nearMiss' | 'unlock' | 'retry' | 'click' | 'collection_open' | 'clawMove' | 'clawClose' | 'toyDrop' | 'bgm_toggle'): void {
    if (!this.enabled || !this.ctx) return;

    switch (name) {
      case 'drop':
        this.playTone(200, 0.3, 'sawtooth', 0.4);
        break;
      case 'grab':
        this.playNoise(0.15, 300);
        break;
      case 'clawMove':
        this.playMechanicalMove(0.2);
        break;
      case 'clawClose':
        this.playMechanicalClick(0.12);
        break;
      case 'toyDrop':
        this.playToyDrop(0.25);
        break;
      case 'success':
        this.playArpeggio([523, 659, 784, 1047], 0.1);
        break;
      case 'fail':
        this.playTone(150, 0.4, 'sawtooth', 0.3);
        break;
      case 'nearMiss':
        this.playTone(350, 0.25, 'square', 0.3);
        break;
      case 'unlock':
        this.playArpeggio([523, 659, 784, 1047, 1319], 0.08);
        break;
      case 'retry':
        this.playTone(440, 0.1, 'sine', 0.2);
        break;
      case 'click':
      case 'collection_open':
      case 'bgm_toggle':
        this.playTone(880, 0.05, 'sine', 0.1);
        break;
    }
  }

  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volumeMultiplier = 1.0
  ): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, this.ctx.currentTime);

    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(this.volume * volumeMultiplier, this.ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + duration);
  }

  private playArpeggio(frequencies: number[], interval: number): void {
    frequencies.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.25, 'sine', 0.8), i * interval * 1000);
    });
  }

  private playNoise(duration: number, frequency: number): void {
    if (!this.ctx) return;
    const bufferSize = this.ctx.sampleRate * duration;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-3 * i / bufferSize);
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = frequency;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(this.volume * 0.4, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);
    noise.start();
  }

  private playMechanicalMove(duration: number): void {
    if (!this.ctx) return;
    // Low-frequency rattle for claw movement
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(80, this.ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(60, this.ctx.currentTime + duration);

    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(this.volume * 0.25, this.ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  private playMechanicalClick(duration: number): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(300, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(150, this.ctx.currentTime + duration);

    gain.gain.setValueAtTime(this.volume * 0.4, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  private playToyDrop(duration: number): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, this.ctx.currentTime + duration);

    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(this.volume * 0.5, this.ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }
}

export const soundManager = new SoundManager();
